<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <div class="card monitoring-filters mb-1">
          <div class="card-body pb-50">
            <div class="form-row align-items-end">
              <div class="form-group col-md-2">
                <label class="small mb-25">{{ $t('Event Type') }}</label>
                <b-form-select v-model="filters.event_type" :options="eventTypeOptions" size="sm" />
              </div>
              <div class="form-group col-md-2">
                <label class="small mb-25">{{ $t('Evento') }}</label>
                <v-select
                    v-model="filters.movias_event_rule_id"
                    :options="rules"
                    :reduce="rule => rule.id"
                    label="name"
                    :placeholder="$t('Todos')"
                    class="monitoring-select"
                />
              </div>
              <div class="form-group col-md-1">
                <label class="small mb-25">{{ $t('Criticidade') }}</label>
                <b-form-select v-model="filters.criticality" :options="criticalityOptions" size="sm" />
              </div>
              <div class="form-group col-md-2">
                <label class="small mb-25">{{ $t('Central') }}</label>
                <v-select
                    v-model="filters.center_id"
                    :options="centers"
                    :reduce="center => center.id"
                    label="name"
                    :placeholder="$t('Todas')"
                    class="monitoring-select"
                    @input="onCenterChange"
                />
              </div>
              <div class="form-group col-md-1">
                <label class="small mb-25">{{ $t('Unidade') }}</label>
                <v-select
                    v-model="filters.unit_id"
                    :options="units"
                    :reduce="unit => unit.id"
                    label="name"
                    :disabled="!filters.center_id"
                    :placeholder="$t('Todas')"
                    class="monitoring-select"
                />
              </div>
              <div class="form-group col-md-2">
                <label class="small mb-25">{{ $t('Vehicle') }}</label>
                <v-select
                    v-model="filters.vehicle_id"
                    :options="vehicleOptions"
                    :reduce="vehicle => vehicle.id"
                    label="label"
                    :placeholder="$t('Todos')"
                    class="monitoring-select"
                />
              </div>
              <div class="form-group col-md-2 text-right">
                <b-button variant="outline-secondary" size="sm" @click="clearFilters">
                  {{ $t('Limpar filtros') }}
                </b-button>
              </div>
              <div class="form-group col-md-2">
                <label class="small mb-25">{{ $t('De') }}</label>
                <b-form-input v-model="filters.from" type="datetime-local" size="sm" />
              </div>
              <div class="form-group col-md-2">
                <label class="small mb-25">{{ $t('Até') }}</label>
                <b-form-input v-model="filters.until" type="datetime-local" size="sm" />
              </div>
              <div class="form-group col-md-8 text-right small realtime-bar">
                <b-button v-if="pendingCount" variant="primary" size="sm" @click="showPending">
                  {{ pendingCount }} {{ $t('novo(s) evento(s)') }}
                </b-button>
                <span :class="live ? 'text-success' : 'text-muted'">
                  <i class="fas fa-circle" /> {{ realtimeStatus }}
                </span>
                <span class="text-muted">
                  {{ $t('Última sincronização') }}: {{ lastSyncLabel }}
                </span>
                <b-button
                    size="sm"
                    :variant="realtimeEnabled ? 'outline-secondary' : 'outline-success'"
                    :title="realtimeEnabled ? $t('Desligar tempo real') : $t('Ligar tempo real')"
                    @click="toggleRealtime"
                >
                  <i :class="realtimeEnabled ? 'fas fa-pause' : 'fas fa-play'" />
                  {{ realtimeEnabled ? $t('Desligar') : $t('Ligar') }}
                </b-button>
                <b-button size="sm" variant="outline-primary" :title="$t('Recarregar')" @click="reload">
                  <i class="fas fa-sync-alt" />
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <base-crud
            :ref="'monitoringsCrud'"
            :crudName="$t('Monitoring')"
            :newText="$t('Add') + '  ' + $t('New')"
            :table="table"
            :columns="columns"
            :options="options"
            :endPoint="endPoint"
            :enableAdd="false"
            :enableEdit="false"
            :enableDelete="false"
            :enableView="false"
            :enableReset="false"
            :dataForm="dataForm"
            :extra="extra"
            @view-map="viewMap"
        >
        </base-crud>

        <b-modal
            v-model="showMapModal"
            :title="$t('Event Position')"
            size="lg"
            ok-only
            :ok-title="$t('Close')"
            ok-variant="secondary"
        >
          <GmapMap
              v-if="mapPosition"
              :zoom="17"
              :center="mapPosition"
              style="width: 100%; height: 450px"
          >
            <GmapMarker :position="mapPosition" :icon="markerIcon" @click="showEventDetails = true" />
          </GmapMap>
          <p v-else class="text-muted mb-0">{{ $t('No position recorded for this event') }}</p>
        </b-modal>

        <b-modal
            v-model="showEventDetails"
            :title="$t('Event Details')"
            ok-only
            :ok-title="$t('Close')"
            ok-variant="secondary"
            size="md"
        >
          <div v-if="selectedRow" class="event-details">
            <div class="ed-row">
              <span class="ed-label"><i class="fas fa-tag"></i> {{ $t('Event Type') }}</span>
              <span class="badge" :class="eventTypeBadgeClass(selectedRow)">
                {{ eventTypeLabel(selectedRow) }}
              </span>
            </div>
            <div class="ed-row">
              <span class="ed-label"><i class="fas fa-car"></i> {{ $t('Vehicle') }}</span>
              <strong>{{ vehicleLabel(selectedRow) || '-' }}</strong>
            </div>
            <div class="ed-row" v-if="selectedRow.movias_event_rule">
              <span class="ed-label"><i class="fas fa-bell"></i> {{ $t('Evento') }}</span>
              <strong>{{ selectedRow.movias_event_rule.name }}</strong>
            </div>
            <div class="ed-row" v-if="selectedRow.criticality">
              <span class="ed-label"><i class="fas fa-exclamation-triangle"></i> {{ $t('Criticidade') }}</span>
              <span class="badge" :class="criticalityBadge(selectedRow.criticality)">
                {{ criticalityLabel(selectedRow.criticality) }}
              </span>
            </div>
            <div class="ed-row" v-if="selectedRow.speed !== null && selectedRow.speed !== undefined">
              <span class="ed-label"><i class="fas fa-tachometer-alt"></i> {{ $t('Velocidade') }}</span>
              <strong>{{ selectedRow.speed }} km/h</strong>
            </div>
            <div class="ed-row" v-if="selectedRow.area">
              <span class="ed-label"><i class="fas fa-map-marked-alt"></i> {{ $t('Area') }}</span>
              <strong>{{ selectedRow.area.name }}</strong>
            </div>
            <div class="ed-row">
              <span class="ed-label"><i class="fas fa-clock"></i> {{ $t('Created At') }}</span>
              <strong>{{ selectedRow.created_at_f }}</strong>
            </div>
            <div class="ed-row" v-if="mapPosition">
              <span class="ed-label"><i class="fas fa-map-marker-alt"></i> {{ $t('Position') }}</span>
              <strong>{{ mapPosition.lat.toFixed(6) }}, {{ mapPosition.lng.toFixed(6) }}</strong>
            </div>
            <div class="ed-description">
              <span class="ed-label"><i class="fas fa-align-left"></i> {{ $t('Event Description') }}</span>
              <p class="mb-0">{{ translateDescription(selectedRow.event_description) }}</p>
            </div>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import vSelect from "vue-select";
import BaseCrud from "../../layouts/Base/BaseCrud";
import { criticalityBadge, criticalityLabel, CRITICALITIES } from "../MoviasEventRule/moviasEvents";

const EMPTY_FILTERS = {
  event_type: null,
  movias_event_rule_id: null,
  criticality: null,
  center_id: null,
  unit_id: null,
  vehicle_id: null,
  from: "",
  until: "",
};

const HIGHLIGHT_MS = 4000;

const REALTIME_KEY = "monitorings.realtime";

function readRealtimePreference() {
  try {
    return localStorage.getItem(REALTIME_KEY) !== "off";
  } catch (e) {
    return true;
  }
}

export default {
  components: {
    BaseCrud,
    vSelect,
  },
  data: function () {
    return {
      table: "monitoringsTable",
      url: "",
      columns: [
        "id",
        "vehicle_id",
        "event_type",
        "criticality",
        "event_description",
        "created_at_f",
        "actions"
      ],
      extra: [
        {
          id: 1,
          label: "",
          class: "btn btn-crud edit",
          title: "Ver Mapa",
          name: "view-map",
          icon: "fas fa-map-marker-alt",
        },
      ],
      filters: { ...EMPTY_FILTERS },
      rules: [],
      centers: [],
      units: [],
      vehicles: [],
      channel: null,
      live: false,
      realtimeEnabled: readRealtimePreference(),
      lastSyncAt: null,
      pendingCount: 0,
      highlighted: {},
      showMapModal: false,
      showEventDetails: false,
      mapPosition: null,
      selectedRow: null,
      markerIcon: {
        url: require("@/assets/images/pin/map-pin-generic.svg"),
        scaledSize: { width: 40, height: 60 },
        anchor: { x: 20, y: 60 },
      },
      tableData: [
        "id",
        "vehicle_id",
        "event_type",
        "criticality",
        "event_description",
        "created_at"
      ],
      options: {
        filterByColumn: true,
        debounce: 1000,
        filterable: ["id"],
        sortable: ["id"],
        orderBy: { column: "id", ascending: false },
        pagination: {
          chunk: 10,
          dropdown: false,
          nav: "scroll",
        },
        perPage: 10,
        perPageValues: [10, 25, 50, 100],
        headings: {
          id: this.$t("id"),
          vehicle_id: this.$t("Vehicle"),
          event_type: this.$t("Event Type"),
          criticality: this.$t("Criticidade"),
          event_description: this.$t("Event Description"),
          created_at_f: this.$t("Created At"),
          actions: this.$t("action"),
        },
        texts: {
          filterBy: this.$t("Filter"),
          defaultOption: this.$t("Select"),
        },
        rowClassCallback: row => (this.highlighted[row.id] ? "monitoring-new" : ""),
        templates: {
          vehicle_id: (h, row) => this.vehicleLabel(row),
          event_type: (h, row) => {
            const text = this.eventTypeLabel(row);
            const badgeClass = this.eventTypeBadgeClass(row);

            if (badgeClass === "badge-secondary") {
              return text;
            }

            return h("span", { class: ["badge", badgeClass] }, text);
          },
          criticality: (h, row) => (row.criticality
              ? h("span", { class: ["badge", criticalityBadge(row.criticality)] }, criticalityLabel(row.criticality))
              : ""),
          event_description: (h, row) => this.translateDescription(row.event_description),
        },
        requestFunction: function (data) {
          const page = this.$parent.$parent.$parent;
          const query = page.query(data.query);
          const requestData = {
            params: {
              ...data,
              ...page.requestFilters(),
              query: "",
              with: ["vehicle", "area", "moviasEventRule"],
            },
          };

          return axios.get(this.url + "?" + query, requestData)
              .then(response => {
                page.markSynced();

                return response;
              })
              .catch(
                  function (e) {
                    this.dispatch("error", e);
                  }.bind(this)
              );
        },
      },
      endPoint: "monitorings",
      dataForm: {},
    };
  },
  computed: {
    companyId() {
      return this.$store.state.user.company_id;
    },
    eventTypeOptions() {
      return [
        { value: null, text: this.$t("Todos") },
        { value: "entry", text: this.$t("Entry") },
        { value: "exit", text: this.$t("Exit") },
        { value: "device_detached", text: this.$t("Device Detached") },
        { value: "device_reattached", text: this.$t("Device Reattached") },
        { value: "movias_event", text: this.$t("Evento personalizado") },
      ];
    },
    criticalityOptions() {
      return [
        { value: null, text: this.$t("Todas") },
        ...Object.entries(CRITICALITIES).map(([value, item]) => ({ value, text: item.label })),
      ];
    },
    realtimeStatus() {
      if (!this.realtimeEnabled) return this.$t("Tempo real desligado");

      return this.live ? this.$t("Tempo real ligado") : this.$t("Conectando...");
    },
    lastSyncLabel() {
      return this.lastSyncAt ? this.lastSyncAt.toLocaleTimeString("pt-BR") : "—";
    },
    vehicleOptions() {
      return this.vehicles
        .filter(vehicle => !this.filters.center_id || Number(vehicle.center_id) === Number(this.filters.center_id))
        .filter(vehicle => !this.filters.unit_id || Number(vehicle.unit_id) === Number(this.filters.unit_id))
        .map(vehicle => ({ id: vehicle.id, label: this.vehicleLabel({ vehicle }) }));
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pendingCount = 0;
        this.refreshTable();
      },
    },
    companyId() {
      this.subscribe();
    },
  },
  mounted() {
    this.loadOptions();
    this.subscribe();
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  methods: {
    criticalityBadge,
    criticalityLabel,
    api(path) {
      return `${this.$store.state.api}${path}`;
    },
    loadOptions() {
      const lists = [
        ["rules", "moviasEventRules"],
        ["centers", "centers"],
        ["vehicles", "vehicles"],
      ];

      lists.forEach(([target, path]) => {
        axios.get(this.api(path), { params: { paginated: false } })
          .then(response => {
            this[target] = response.data.data || [];
          })
          .catch(() => {
            this[target] = [];
          });
      });
    },
    onCenterChange() {
      this.filters.unit_id = null;
      this.units = [];

      if (!this.filters.center_id) return;

      axios.get(this.api("units"), { params: { paginated: false, center_id: this.filters.center_id } })
        .then(response => {
          this.units = response.data.data || [];
        })
        .catch(error => this.$error(error));
    },
    clearFilters() {
      this.filters = { ...EMPTY_FILTERS };
      this.units = [];
    },
    toServerDate(value) {
      return value ? `${value.replace("T", " ")}:00`.slice(0, 19) : "";
    },
    requestFilters() {
      const params = {};

      ["event_type", "movias_event_rule_id", "criticality", "center_id", "unit_id", "vehicle_id"].forEach(key => {
        if (this.filters[key] !== null && this.filters[key] !== "") {
          params[key] = this.filters[key];
        }
      });

      if (this.filters.from) params.from = this.toServerDate(this.filters.from);
      if (this.filters.until) params.until = this.toServerDate(this.filters.until);

      return params;
    },
    serverTable() {
      return this.$refs.monitoringsCrud ? this.$refs.monitoringsCrud.$refs.table : null;
    },
    refreshTable() {
      const table = this.serverTable();

      if (table) table.refresh();
    },
    markSynced() {
      this.lastSyncAt = new Date();
    },
    reload() {
      this.pendingCount = 0;
      this.refreshTable();
    },
    toggleRealtime() {
      this.realtimeEnabled = !this.realtimeEnabled;

      try {
        localStorage.setItem(REALTIME_KEY, this.realtimeEnabled ? "on" : "off");
      } catch (e) {
        // preferência só local; sem storage o padrão ligado vale
      }

      if (this.realtimeEnabled) {
        this.subscribe();
        this.reload();
      } else {
        this.unsubscribe();
        this.pendingCount = 0;
      }
    },
    connection() {
      return window.Echo && window.Echo.connector && window.Echo.connector.pusher
        ? window.Echo.connector.pusher.connection
        : null;
    },
    onConnectionChange() {
      const connection = this.connection();

      this.live = !!this.channel && !!connection && connection.state === "connected";
    },
    subscribe() {
      if (!window.Echo || !this.companyId || !this.realtimeEnabled) return;

      const channel = `monitorings.${this.companyId}`;

      if (this.channel === channel) return;

      this.unsubscribe();
      this.channel = channel;
      window.Echo.channel(channel).listen(".monitoring-created", this.onMonitoringCreated);

      const connection = this.connection();

      if (connection) connection.bind("state_change", this.onConnectionChange);

      this.onConnectionChange();
    },
    unsubscribe() {
      const connection = this.connection();

      if (connection) connection.unbind("state_change", this.onConnectionChange);

      if (this.channel && window.Echo) {
        window.Echo.channel(this.channel).stopListening(".monitoring-created", this.onMonitoringCreated);
        window.Echo.leaveChannel(this.channel);
      }

      this.channel = null;
      this.live = false;
    },
    matchesFilters(row) {
      const filters = this.requestFilters();
      const vehicle = row.vehicle || {};
      const direct = ["event_type", "movias_event_rule_id", "criticality", "vehicle_id"];

      if (direct.some(key => filters[key] !== undefined && String(row[key]) !== String(filters[key]))) {
        return false;
      }

      if (filters.center_id !== undefined && Number(vehicle.center_id) !== Number(filters.center_id)) return false;
      if (filters.unit_id !== undefined && Number(vehicle.unit_id) !== Number(filters.unit_id)) return false;
      if (filters.from && row.created_at < filters.from) return false;
      if (filters.until && row.created_at > filters.until) return false;

      return true;
    },
    hasColumnFilters(table) {
      return Object.values(table.query || {}).some(value => value !== "" && value !== null && value !== undefined);
    },
    onMonitoringCreated(row) {
      const table = this.serverTable();

      if (!table || !this.matchesFilters(row)) return;

      if (table.page !== 1 || this.hasColumnFilters(table)) {
        this.pendingCount += 1;
        this.markSynced();

        return;
      }

      if (table.data.some(item => Number(item.id) === Number(row.id))) return;

      table.data.unshift(row);

      if (table.data.length > table.limit) {
        table.data.pop();
      }

      table.count += 1;
      this.markSynced();
      this.highlight(row.id);
    },
    highlight(id) {
      this.$set(this.highlighted, id, true);
      setTimeout(() => this.$delete(this.highlighted, id), HIGHLIGHT_MS);
    },
    showPending() {
      this.pendingCount = 0;

      const table = this.serverTable();

      if (table && table.page !== 1) {
        table.setPage(1);
      } else {
        this.refreshTable();
      }
    },
    query: function (query) {
      const columns = {
        id: "id",
      };

      let filters = "";

      $.each(query, function (index, value) {
        if (!value || !columns[index]) return;
        filters += columns[index] + "=" + encodeURIComponent(value) + "&";
      });

      return filters;
    },
    vehicleLabel(row) {
      const vehicle = row.vehicle;

      if (!vehicle) return "";

      return vehicle.name && vehicle.name !== vehicle.license_plate && vehicle.license_plate
        ? `${vehicle.license_plate} — ${vehicle.name}`
        : (vehicle.license_plate || vehicle.name || "");
    },
    viewMap({ row }) {
      this.selectedRow = row;
      this.mapPosition = this.resolvePosition(row);
      this.showMapModal = true;
    },
    eventTypeLabel(row) {
      const eventType = typeof row === "string" ? row : row.event_type;

      if (eventType === "movias_event" && row.movias_event_rule) {
        return row.movias_event_rule.name;
      }

      const translations = {
        entry: this.$t("Entry"),
        exit: this.$t("Exit"),
        device_detached: this.$t("Device Detached"),
        device_reattached: this.$t("Device Reattached"),
        movias_event: this.$t("Evento personalizado"),
      };
      return translations[eventType] || eventType;
    },
    eventTypeBadgeClass(row) {
      const eventType = typeof row === "string" ? row : row.event_type;
      const badgeClasses = {
        device_detached: "badge-danger",
        device_reattached: "badge-success",
        movias_event: "badge-primary",
      };
      return badgeClasses[eventType] || "badge-secondary";
    },
    translateDescription(description) {
      if (!description) return "";

      let translated = description;

      const translations = {
        "Initial log: Vehicle entry area": this.$t("Initial log: Vehicle entry area"),
        "Initial log: Vehicle exit area": this.$t("Initial log: Vehicle exit area"),
        "State changed: Vehicle entry area": this.$t("State changed: Vehicle entry area"),
        "State changed: Vehicle exit area": this.$t("State changed: Vehicle exit area"),
        " at position ": " " + this.$t("at position") + " ",
      };

      for (const [key, value] of Object.entries(translations)) {
        if (translated.includes(key)) {
          translated = translated.replace(key, value);
        }
      }

      return translated;
    },
    resolvePosition(row) {
      if (row.lat != null && row.long != null) {
        return { lat: parseFloat(row.lat), lng: parseFloat(row.long) };
      }

      // eventos antigos de entrada/saída de área não têm lat/long em coluna própria,
      // só embutido no texto: "at position (lat, long)"
      const match = row.event_description
          ? row.event_description.match(/\(([-\d.]+),\s*([-\d.]+)\)/)
          : null;

      if (!match) return null;

      return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
    },
  },
};
</script>

<style scoped>
.VuePagination {
  text-align: center;
}

.monitoring-filters .form-group {
  margin-bottom: 0.75rem;
}

.realtime-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

::v-deep .monitoring-select .vs__dropdown-toggle {
  min-height: 30px;
}

::v-deep tr.monitoring-new td {
  animation: monitoring-flash 4s ease-out;
}

@keyframes monitoring-flash {
  from {
    background-color: rgba(40, 199, 111, 0.25);
  }
  to {
    background-color: transparent;
  }
}

.event-details {
  font-size: 14px;
}

.ed-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #f0f0f0;
}

.ed-row:last-of-type {
  border-bottom: none;
}

.ed-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
}

.ed-label .fas {
  width: 16px;
  text-align: center;
  color: #8a8f98;
}

.ed-description {
  padding-top: 12px;
  margin-top: 4px;
  border-top: 1px solid #f0f0f0;
}

.ed-description p {
  color: #333;
  line-height: 1.5;
  margin-top: 6px;
}
</style>
